import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useRef, useState } from "react";
import { CLASSES_DE_ALINHAMENTO, TAMANHOS_DO_TEXTO, sanearHtml } from "@noesis/shared";
import { Icone } from "../componentes/Icone.js";
import { useCursorFluido } from "./cursor-fluido.js";
import { EXTENSOES_DO_CADERNO, NIVEIS, type NivelDePaleta } from "./extensoes.js";
import {
  IconeAbrirMenu,
  IconeAlinharCentro,
  IconeAlinharDireita,
  IconeAlinharEsquerda,
  IconeAumentarRecuo,
  IconeBlocoDeCodigo,
  IconeCitacao,
  IconeCorDoTexto,
  IconeDesfazer,
  IconeDiminuirRecuo,
  IconeForte,
  IconeItalico,
  IconeJustificar,
  IconeLimparFormatacao,
  IconeLink,
  IconeLinhaHorizontal,
  IconeListaDeTarefas,
  IconeListaNumerada,
  IconeListaSimples,
  IconeMarcaTexto,
  IconeRefazer,
  IconeRemoverLink,
  IconeRiscado,
  IconeSublinhado,
  IconeTabela,
} from "../componentes/icones.js";

/**
 * O editor do caderno — família ProseMirror, com marcas próprias.
 *
 * **Por que marcas próprias.** As extensões prontas de cor, realce e
 * alinhamento serializam o valor como estilo inline (`style="color: …"`), e a
 * CSP do Noesis é `style-src 'self'` sem `style-src-attr`: o navegador
 * bloquearia o estilo e a falha apareceria no conteúdo do usuário — um
 * parágrafo sem cor, sem erro nenhum no console. As de `extensoes.ts` devolvem
 * classe do vocabulário fechado da RN-40, e o que está aprovado no portão de
 * CSP é essa combinação, não "o Tiptap". O portão vive em
 * `portao-csp.portao.test.ts` e quebra se alguém acrescentar uma extensão que
 * volte a escrever estilo.
 *
 * **A barra é uma paleta, não um seletor de valor.** Quatro cores, quatro
 * realces, três tamanhos e três alinhamentos. Um seletor livre seria o usuário
 * reescrevendo a tipografia do app um parágrafo por vez, e nenhum par de cor e
 * fundo teria contraste verificado.
 *
 * **Todo alvo mostra o efeito que aplica.** As amostras de cor e de realce são
 * pintadas na própria cor — quatro "A" idênticos não dizem nada, e eram o que
 * havia: as classes do vocabulário estavam no botão, mas perdiam para
 * `.editor-rico__botao` na cascata, que reatribui `color` e `background`. O
 * conserto é seletor de duas classes na folha, e não classe nova aqui.
 *
 * **Onze alvos à vista, o resto em dois menus.** A paleta inteira exposta de uma
 * vez são dezenove alvos, que numa coluna de apoio empilham em sete linhas e
 * deixam a barra mais alta que a área de escrita. Cor, realce e tamanho moram no
 * menu de aparência; os alinhamentos, no de alinhamento. Ver `Barra` e a ADR
 * 022 — é a contagem de alvos desta barra que dá a largura do painel do caderno
 * na sessão.
 *
 * **A área de escrita é uma superfície de `conteudo-rico`**, a mesma da
 * leitura. Não é economia de CSS: a forma dos nós estruturais — lista, citação,
 * título, código — mora numa seção só da folha, e escrever a mesma coisa duas
 * vezes é a chance de a lista aparecer num lado e não no outro. Tirar esta
 * classe daqui apaga a formatação dentro do editor, e o portão da forma
 * (`estilo-dos-comandos.portao.test.ts`) lê a classe deste componente montado
 * justamente para não deixar isso passar.
 *
 * O que sai daqui passa pelo saneador antes de subir. Não é desconfiança do
 * editor: é que o `onUpdate` também dispara depois de uma colagem, e o que vem
 * da área de transferência é HTML de qualquer lugar.
 */
export function EditorRico({
  html,
  aoMudar,
  rotulo,
  compacta,
  aoSalvar,
  rodape,
}: {
  html: string;
  aoMudar: (html: string) => void;
  rotulo: string;
  /** Barra em modo estreito — o painel da sessão de estudo. */
  compacta?: boolean;
  /** Ctrl+S: força a gravação sem esperar o debounce. */
  aoSalvar?: () => void;
  /** Linha discreta no pé do editor — os itens vinculados, por exemplo. */
  rodape?: React.ReactNode;
}) {
  const editor = useEditor({
    extensions: [
      /*
       * `openOnClick: false` na edição, e não é preferência: com ele ligado um
       * clique dentro de um link abre a aba em vez de pôr o cursor ali, e não há
       * como editar o texto de um link. A leitura é outro componente
       * (`ConteudoRico`), e lá o link abre normalmente.
       */
      StarterKit.configure({ link: { openOnClick: false } }),
      ...EXTENSOES_DO_CADERNO,
    ],
    content: html,
    editorProps: {
      attributes: {
        class: "editor-rico__area conteudo-rico leitura",
        /*
         * `role` e `aria-multiline` junto com o nome, e não só o nome.
         * O ProseMirror monta um `<div contenteditable>` sem papel nenhum, e
         * `aria-label` num `div` sem papel válido é atributo proibido — a régua
         * de interface acusou `aria-prohibited-attr` na tela do caderno. Com
         * `textbox` o nome passa a ser permitido e o leitor de tela anuncia a
         * área pelo que ela é: um campo de texto de várias linhas.
         */
        role: "textbox",
        "aria-multiline": "true",
        "aria-label": rotulo,
      },
      handleKeyDown: (_view, evento) => {
        // Ctrl+S força a gravação. `preventDefault` é o ponto: sem ele o
        // navegador abre "salvar página", que é a última coisa que se quer.
        if ((evento.ctrlKey || evento.metaKey) && evento.key.toLowerCase() === "s") {
          evento.preventDefault();
          aoSalvar?.();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => aoMudar(sanearHtml(editor.getHTML())),
  });

  /*
   * Conteúdo que chega de fora — a nota carregada do servidor, ou a versão do
   * servidor adotada num conflito. `setContent` sem isto rodaria a cada tecla,
   * porque `html` volta pelo `aoMudar`: a comparação é o que separa "o pai me
   * mandou outro texto" de "o pai está ecoando o meu".
   */
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (sanearHtml(editor.getHTML()) === html) return;
    editor.commands.setContent(html, { emitUpdate: false });
  }, [editor, html]);

  const { fluido, superficie, cursor } = useCursorFluido(editor);

  if (!editor) return null;

  return (
    <div className="editor-rico">
      <Barra editor={editor} compacta={compacta} />
      {/*
        A superfície existe para o cursor fluido ter de onde medir: o elemento
        dele é **irmão** da área de escrita, nunca filho. Dentro do
        contenteditable o ProseMirror trataria o nó como conteúdo alheio e o
        tiraria de lá na primeira transação.
      */}
      <div
        className={
          fluido
            ? "editor-rico__superficie editor-rico__superficie--fluida"
            : "editor-rico__superficie"
        }
        ref={superficie}
      >
        <EditorContent editor={editor} />
        {fluido && <span className="editor-rico__cursor" aria-hidden="true" ref={cursor} />}
      </div>
      {rodape}
    </div>
  );
}

/**
 * A barra de ferramentas — dez grupos, na ordem da Fase 2.
 *
 * **Ela deixou de ser uma barra com dois menus e virou uma barra de verdade.**
 * A versão anterior expunha onze alvos e escondia cor, realce, tamanho e
 * alinhamento atrás de dois gatilhos, porque a paleta inteira aberta empilhava
 * em sete linhas numa coluna de apoio. O caderno agora é tela de duas colunas,
 * com o editor ocupando a maior delas: cabe a barra inteira, e o que continua
 * em menu é o que é **escolha de valor** — tipo de bloco, fonte, tamanho, cor,
 * realce e tabela —, não o que é comando.
 *
 * A ordem dos dez grupos não é arbitrária: ela vai do que desfaz para o que
 * estrutura, passando pelo que formata trecho e pelo que formata bloco. É a
 * ordem em que a mão procura.
 *
 * **Nenhum alvo escreve `style`.** Continua valendo integralmente: cor, realce,
 * tamanho, fonte, alinhamento, recuo e tarefa saem como classe do vocabulário
 * fechado, a tabela vai sem redimensionamento (que é o que produziria
 * `style="width"`), e `portao-csp.portao.test.ts` roda todo comando desta barra
 * e falha se um `style=` aparecer na serialização.
 */
export function Barra({ editor, compacta }: { editor: Editor; compacta?: boolean }) {
  const corAtiva = NIVEIS.some((n) => editor.isActive("ricoCor", { nivel: n }));
  const realceAtivo = NIVEIS.some((n) => editor.isActive("ricoRealce", { nivel: n }));
  const alinhado = CLASSES_DE_ALINHAMENTO.some((c) => editor.isActive({ alinhamento: c }));

  const tamanhoAtual = TAMANHOS_DO_TEXTO.find((n) => editor.isActive("ricoTamanho", { escala: n })) ?? null;
  const fonteAtual = editor.isActive("ricoFonte", { familia: "mono" })
    ? "mono"
    : editor.isActive("ricoFonte", { familia: "serifa" })
      ? "serifa"
      : null;

  const nivelDoTitulo = ([1, 2, 3] as const).find((n) => editor.isActive("heading", { level: n })) ?? null;
  const naTabela = editor.isActive("table");

  return (
    <div
      className={compacta ? "editor-rico__barra editor-rico__barra--compacta" : "editor-rico__barra"}
      role="toolbar"
      aria-label="Formatação"
    >
      <Grupo rotulo="Histórico">
        <Botao
          rotulo="Desfazer"
          atalho="Ctrl+Z"
          glifo={IconeDesfazer}
          desabilitado={!editor.can().undo()}
          aoTocar={() => editor.chain().focus().undo().run()}
        />
        <Botao
          rotulo="Refazer"
          atalho="Ctrl+Shift+Z"
          glifo={IconeRefazer}
          desabilitado={!editor.can().redo()}
          aoTocar={() => editor.chain().focus().redo().run()}
        />
      </Grupo>

      <Grupo rotulo="Tipo de bloco">
        <MenuDeTexto
          id="menu-bloco-do-caderno"
          rotulo="Tipo de bloco"
          valor={nivelDoTitulo ? `Título ${nivelDoTitulo}` : "Texto normal"}
          largura="g"
        >
          <Opcao
            rotulo="Texto normal"
            ativo={nivelDoTitulo === null}
            aoTocar={() => editor.chain().focus().setParagraph().run()}
          />
          {([1, 2, 3] as const).map((n) => (
            <Opcao
              key={n}
              rotulo={`Título ${n}`}
              classe={`editor-rico__opcao--h${n}`}
              ativo={nivelDoTitulo === n}
              aoTocar={() => editor.chain().focus().toggleHeading({ level: n }).run()}
            />
          ))}
        </MenuDeTexto>
      </Grupo>

      <Grupo rotulo="Fonte">
        <MenuDeTexto
          id="menu-fonte-do-caderno"
          rotulo="Fonte"
          valor={fonteAtual === "mono" ? "Mono" : fonteAtual === "serifa" ? "Atkinson" : "Kalam"}
          largura="m"
        >
          <Opcao
            rotulo="Kalam"
            ativo={fonteAtual === null}
            aoTocar={() => editor.chain().focus().definirFonte(null).run()}
          />
          <Opcao
            rotulo="Atkinson"
            classe="rico-fonte-serifa"
            ativo={fonteAtual === "serifa"}
            aoTocar={() => editor.chain().focus().definirFonte("serifa").run()}
          />
          <Opcao
            rotulo="Mono"
            classe="rico-fonte-mono"
            ativo={fonteAtual === "mono"}
            aoTocar={() => editor.chain().focus().definirFonte("mono").run()}
          />
        </MenuDeTexto>
      </Grupo>

      <Grupo rotulo="Tamanho">
        <MenuDeTexto
          id="menu-tamanho-do-caderno"
          rotulo="Tamanho do texto"
          valor={tamanhoAtual === null ? "16" : String(tamanhoAtual)}
          largura="p"
        >
          {TAMANHOS_DO_TEXTO.map((n) => (
            <Opcao
              key={n}
              rotulo={String(n)}
              ativo={tamanhoAtual === n || (tamanhoAtual === null && n === 16)}
              aoTocar={() =>
                n === 16
                  ? editor.chain().focus().limparTamanho().run()
                  : editor.chain().focus().definirTamanho(n).run()
              }
            />
          ))}
        </MenuDeTexto>
      </Grupo>

      <Grupo rotulo="Ênfase">
        <Botao
          rotulo="Negrito"
          atalho="Ctrl+B"
          glifo={IconeForte}
          ativo={editor.isActive("bold")}
          aoTocar={() => editor.chain().focus().toggleBold().run()}
        />
        <Botao
          rotulo="Itálico"
          atalho="Ctrl+I"
          glifo={IconeItalico}
          ativo={editor.isActive("italic")}
          aoTocar={() => editor.chain().focus().toggleItalic().run()}
        />
        <Botao
          rotulo="Sublinhado"
          atalho="Ctrl+U"
          glifo={IconeSublinhado}
          ativo={editor.isActive("underline")}
          aoTocar={() => editor.chain().focus().toggleUnderline().run()}
        />
        <Botao
          rotulo="Tachado"
          glifo={IconeRiscado}
          ativo={editor.isActive("strike")}
          aoTocar={() => editor.chain().focus().toggleStrike().run()}
        />
      </Grupo>

      <Grupo rotulo="Cor">
        {/*
          Cada alvo é a amostra **pintada na cor que aplica**. "Cor 3" não diz
          nada a quem olha, e a paleta é pequena o bastante para o desenho ser a
          etiqueta; o nome continua no `aria-label`.
        */}
        <Menu id="menu-cor-do-caderno" rotulo="Cor do texto" glifo={IconeCorDoTexto} emUso={corAtiva}>
          <Secao rotulo="Cor do texto">
            {NIVEIS.map((n) => (
              <Amostra
                key={n}
                classe={`rico-cor-${n}`}
                rotulo={`Cor ${n}`}
                ativo={editor.isActive("ricoCor", { nivel: n })}
                aoTocar={() => editor.chain().focus().definirCor(n).run()}
              />
            ))}
            <Amostra
              classe="editor-rico__amostra--nenhuma"
              rotulo="Padrão"
              ativo={!corAtiva}
              aoTocar={() => editor.chain().focus().limparCor().run()}
            />
          </Secao>
        </Menu>

        <Menu id="menu-realce-do-caderno" rotulo="Marca-texto" glifo={IconeMarcaTexto} emUso={realceAtivo}>
          <Secao rotulo="Marca-texto">
            {NIVEIS.map((n) => (
              <Amostra
                key={n}
                classe={`rico-realce-${n}`}
                rotulo={`Realce ${n}`}
                ativo={editor.isActive("ricoRealce", { nivel: n })}
                aoTocar={() => editor.chain().focus().definirRealce(n).run()}
              />
            ))}
            <Amostra
              classe="editor-rico__amostra--nenhuma"
              rotulo="Nenhum"
              ativo={!realceAtivo}
              aoTocar={() => editor.chain().focus().limparRealce().run()}
            />
          </Secao>
        </Menu>
      </Grupo>

      <Grupo rotulo="Listas e recuo">
        <Botao
          rotulo="Lista com marcadores"
          glifo={IconeListaSimples}
          ativo={editor.isActive("bulletList")}
          aoTocar={() => editor.chain().focus().toggleBulletList().run()}
        />
        <Botao
          rotulo="Lista numerada"
          glifo={IconeListaNumerada}
          ativo={editor.isActive("orderedList")}
          aoTocar={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <Botao
          rotulo="Lista de tarefas"
          glifo={IconeListaDeTarefas}
          ativo={editor.isActive("taskList")}
          aoTocar={() => editor.chain().focus().toggleTaskList().run()}
        />
        <Botao
          rotulo="Diminuir recuo"
          atalho="Shift+Tab"
          glifo={IconeDiminuirRecuo}
          aoTocar={() => recuar(editor, -1)}
        />
        <Botao rotulo="Aumentar recuo" atalho="Tab" glifo={IconeAumentarRecuo} aoTocar={() => recuar(editor, 1)} />
      </Grupo>

      <Grupo rotulo="Alinhamento">
        {/* Sem classe é o alinhamento padrão: um vocabulário que nomeia o
            padrão obriga todo bloco a carregar uma classe para dizer "nada de
            especial". */}
        <Botao
          rotulo="Alinhar à esquerda"
          glifo={IconeAlinharEsquerda}
          ativo={!alinhado}
          aoTocar={() => editor.chain().focus().definirAlinhamento(null).run()}
        />
        <Botao
          rotulo="Centralizar"
          glifo={IconeAlinharCentro}
          ativo={editor.isActive({ alinhamento: "rico-centro" })}
          aoTocar={() => editor.chain().focus().definirAlinhamento("rico-centro").run()}
        />
        <Botao
          rotulo="Alinhar à direita"
          glifo={IconeAlinharDireita}
          ativo={editor.isActive({ alinhamento: "rico-direita" })}
          aoTocar={() => editor.chain().focus().definirAlinhamento("rico-direita").run()}
        />
        <Botao
          rotulo="Justificar"
          glifo={IconeJustificar}
          ativo={editor.isActive({ alinhamento: "rico-justificado" })}
          aoTocar={() => editor.chain().focus().definirAlinhamento("rico-justificado").run()}
        />
      </Grupo>

      <Grupo rotulo="Blocos">
        <Botao
          rotulo="Citação"
          glifo={IconeCitacao}
          ativo={editor.isActive("blockquote")}
          aoTocar={() => editor.chain().focus().toggleBlockquote().run()}
        />
        <Botao
          rotulo="Bloco de código"
          glifo={IconeBlocoDeCodigo}
          ativo={editor.isActive("codeBlock")}
          aoTocar={() => editor.chain().focus().toggleCodeBlock().run()}
        />
        <Botao
          rotulo="Linha horizontal"
          glifo={IconeLinhaHorizontal}
          aoTocar={() => editor.chain().focus().setHorizontalRule().run()}
        />
        <Menu id="menu-tabela-do-caderno" rotulo="Tabela" glifo={IconeTabela} emUso={naTabela}>
          <Secao rotulo="Tabela" empilhado>
            <Opcao
              rotulo="Inserir tabela 3×3"
              ativo={false}
              aoTocar={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
            />
            <Opcao rotulo="Linha acima" ativo={false} desabilitado={!naTabela} aoTocar={() => editor.chain().focus().addRowBefore().run()} />
            <Opcao rotulo="Linha abaixo" ativo={false} desabilitado={!naTabela} aoTocar={() => editor.chain().focus().addRowAfter().run()} />
            <Opcao rotulo="Coluna antes" ativo={false} desabilitado={!naTabela} aoTocar={() => editor.chain().focus().addColumnBefore().run()} />
            <Opcao rotulo="Coluna depois" ativo={false} desabilitado={!naTabela} aoTocar={() => editor.chain().focus().addColumnAfter().run()} />
            <Opcao rotulo="Remover linha" ativo={false} desabilitado={!naTabela} aoTocar={() => editor.chain().focus().deleteRow().run()} />
            <Opcao rotulo="Remover coluna" ativo={false} desabilitado={!naTabela} aoTocar={() => editor.chain().focus().deleteColumn().run()} />
            <Opcao rotulo="Excluir tabela" ativo={false} desabilitado={!naTabela} aoTocar={() => editor.chain().focus().deleteTable().run()} />
          </Secao>
        </Menu>
      </Grupo>

      <Grupo rotulo="Link e limpeza">
        <PopoverDeLink editor={editor} />
        <Botao
          rotulo="Remover link"
          glifo={IconeRemoverLink}
          desabilitado={!editor.isActive("link")}
          aoTocar={() => editor.chain().focus().unsetLink().run()}
        />
        <Botao
          rotulo="Limpar formatação"
          glifo={IconeLimparFormatacao}
          aoTocar={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        />
      </Grupo>
    </div>
  );
}

/**
 * Recuar — e **duas operações diferentes com o mesmo gesto**.
 *
 * Dentro de lista, recuar é aninhar: `sinkListItem` e `liftListItem` mexem na
 * estrutura do documento, e é o que o Tab faz lá. Fora dela não há estrutura
 * para aninhar, e o recuo é a classe da extensão `Recuo`. Um botão só, porque
 * para quem usa é uma decisão só.
 */
function recuar(editor: Editor, passo: 1 | -1): void {
  const item = editor.isActive("taskItem") ? "taskItem" : editor.isActive("listItem") ? "listItem" : null;
  if (item) {
    const c = editor.chain().focus();
    if (passo === 1) c.sinkListItem(item).run();
    else c.liftListItem(item).run();
    return;
  }
  if (passo === 1) editor.chain().focus().aumentarRecuo().run();
  else editor.chain().focus().diminuirRecuo().run();
}

/**
 * Inserir link — popover com um campo de endereço.
 *
 * Ele não usa `prompt()`: diálogo do navegador tira o foco da página inteira, e
 * quando volta a seleção do ProseMirror já se foi. O campo vive num `popover`
 * nativo, pela mesma razão que os menus da barra: a *top layer* não é recortada
 * pelo `overflow` do painel.
 *
 * A seleção é guardada **antes** de o campo receber o foco, e restaurada na
 * hora de aplicar: escrever num `<input>` move o foco para fora da área de
 * escrita, e sem a posição guardada o link cairia no fim do documento.
 */
function PopoverDeLink({ editor }: { editor: Editor }) {
  const id = "popover-link-do-caderno";
  const painel = useRef<HTMLDivElement>(null);
  const gatilho = useRef<HTMLButtonElement>(null);
  const campo = useRef<HTMLInputElement>(null);
  const faixa = useRef<{ de: number; ate: number } | null>(null);
  const [url, setUrl] = useState("");
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    const caixa = painel.current;
    const alvo = gatilho.current;
    if (!caixa || !alvo) return;
    const aoAlternar = (e: Event) => {
      const evento = e as ToggleEvent;
      const abrindo = evento.newState === "open";
      if (abrindo) {
        const r = alvo.getBoundingClientRect();
        caixa.style.setProperty("--menu-topo", `${Math.round(r.bottom)}px`);
        caixa.style.setProperty("--menu-direita", `${Math.round(window.innerWidth - r.right)}px`);
        const { from, to } = editor.state.selection;
        faixa.current = { de: from, ate: to };
        setUrl(String(editor.getAttributes("link").href ?? ""));
        // Depois da pintura: antes dela o campo ainda não está na top layer.
        requestAnimationFrame(() => campo.current?.focus());
      }
      setAberto(abrindo);
    };
    caixa.addEventListener("beforetoggle", aoAlternar);
    return () => caixa.removeEventListener("beforetoggle", aoAlternar);
  }, [editor]);

  function aplicar() {
    const limpo = url.trim();
    const f = faixa.current;
    const corrente = editor.chain().focus();
    if (f) corrente.setTextSelection({ from: f.de, to: f.ate });
    if (limpo === "") corrente.unsetLink().run();
    else corrente.setLink({ href: limpo }).run();
    painel.current?.hidePopover();
  }

  return (
    <>
      <button
        type="button"
        ref={gatilho}
        className={
          editor.isActive("link")
            ? "editor-rico__botao editor-rico__gatilho editor-rico__gatilho--em-uso"
            : "editor-rico__botao editor-rico__gatilho"
        }
        aria-label="Inserir link"
        title="Inserir link · Ctrl+K"
        aria-haspopup="dialog"
        aria-expanded={aberto}
        popoverTarget={id}
        onMouseDown={impedirRoubo}
      >
        <Icone glifo={IconeLink} papel="acao" />
      </button>

      <div id={id} popover="auto" ref={painel} className="editor-rico__menu editor-rico__link" aria-label="Inserir link">
        <label className="campo">
          <span className="campo__rotulo">Endereço</span>
          <input
            ref={campo}
            className="campo__entrada"
            type="url"
            inputMode="url"
            placeholder="https://"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                aplicar();
              }
            }}
          />
        </label>
        {/* O guarda do `mousedown` vale aqui também: a seleção guardada é a que
            `aplicar` restaura, e deixar o navegador mexer no foco antes do
            clique é justamente o que a perderia. */}
        <button type="button" className="botao botao--compacto" onMouseDown={impedirRoubo} onClick={aplicar}>
          Aplicar
        </button>
      </div>
    </>
  );
}

/**
 * Um menu cujo gatilho mostra o **valor atual** em texto, e não um ícone.
 *
 * Tipo de bloco, fonte e tamanho são escolhas de valor: o que importa saber de
 * relance é em que valor se está, e um ícone não diz "Título 2" nem "18". Os
 * três têm larguras diferentes porque o conteúdo é diferente — dois dígitos não
 * precisam do espaço de "Texto normal".
 */
function MenuDeTexto({
  id,
  rotulo,
  valor,
  largura,
  children,
}: {
  id: string;
  rotulo: string;
  valor: string;
  largura: "p" | "m" | "g";
  children: React.ReactNode;
}) {
  const gatilho = useRef<HTMLButtonElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    const caixa = painel.current;
    const alvo = gatilho.current;
    if (!caixa || !alvo) return;
    const aoAlternar = (e: Event) => {
      const evento = e as ToggleEvent;
      const abrindo = evento.newState === "open";
      if (abrindo) {
        const r = alvo.getBoundingClientRect();
        caixa.style.setProperty("--menu-topo", `${Math.round(r.bottom)}px`);
        caixa.style.setProperty("--menu-direita", `${Math.round(window.innerWidth - r.right)}px`);
      }
      setAberto(abrindo);
    };
    caixa.addEventListener("beforetoggle", aoAlternar);
    return () => caixa.removeEventListener("beforetoggle", aoAlternar);
  }, []);

  return (
    <>
      <button
        type="button"
        ref={gatilho}
        className={`editor-rico__gatilho-texto editor-rico__gatilho-texto--${largura}`}
        aria-label={rotulo}
        title={rotulo}
        aria-haspopup="true"
        aria-expanded={aberto}
        popoverTarget={id}
        onMouseDown={impedirRoubo}
      >
        <span className="editor-rico__valor">{valor}</span>
        <Icone glifo={IconeAbrirMenu} papel="acao" tamanho="p" />
      </button>

      <div id={id} popover="auto" ref={painel} className="editor-rico__menu" aria-label={rotulo}>
        <div className="editor-rico__opcoes" role="group" aria-label={rotulo}>
          {children}
        </div>
      </div>
    </>
  );
}

/** Uma linha de menu com rótulo em texto. */
function Opcao({
  rotulo,
  classe,
  ativo,
  desabilitado,
  aoTocar,
}: {
  rotulo: string;
  classe?: string;
  ativo: boolean;
  desabilitado?: boolean;
  aoTocar: () => void;
}) {
  return (
    <button
      type="button"
      className={classe ? `editor-rico__opcao ${classe}` : "editor-rico__opcao"}
      aria-pressed={ativo}
      disabled={desabilitado ?? false}
      onMouseDown={impedirRoubo}
      onClick={aoTocar}
    >
      {rotulo}
    </button>
  );
}

/**
 * Um menu da barra — `popover` nativo, posicionado por medida publicada.
 *
 * **Por que `popover` e não uma caixa posicionada por `absolute`.** O painel do
 * caderno na sessão tem `overflow-y: auto` para caber na altura da janela, e
 * `overflow` diferente de `visible` **recorta** qualquer descendente
 * posicionado. Um menu por `absolute` nasceria cortado na borda do painel, que
 * é justamente o defeito que a barra em menus existe para evitar. O `popover`
 * pinta na *top layer*, fora da árvore de recorte, e traz de graça o que
 * teríamos de escrever à mão: fechar ao clicar fora, fechar no `Esc`, e um só
 * menu aberto por vez.
 *
 * **A posição vem de medida, não de `absolute`.** Na *top layer* não há pai a
 * que se ancorar, então o lugar é publicado como custom property a partir do
 * `getBoundingClientRect()` do gatilho — o mesmo idioma de `altura-medida.ts`.
 * `setProperty` é CSSOM, e CSSOM não é o que a CSP barra: o que a política
 * bloqueia é `style=` vindo de marcação, e é do conteúdo do usuário que fala o
 * invariante 17. Nada disto é gravado em nota nenhuma.
 *
 * O menu é **encostado pela direita do gatilho**, e não pela esquerda: os dois
 * gatilhos ficam no fim da barra, e alinhar pela direita mantém o menu dentro
 * da tela sem precisar medir a largura dele antes de mostrá-lo.
 */
function Menu({
  id,
  rotulo,
  glifo,
  emUso,
  children,
}: {
  id: string;
  rotulo: string;
  glifo: Parameters<typeof Icone>[0]["glifo"];
  /** Alguma marca deste menu está aplicada na seleção. */
  emUso: boolean;
  children: React.ReactNode;
}) {
  const gatilho = useRef<HTMLButtonElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    const caixa = painel.current;
    const alvo = gatilho.current;
    if (!caixa || !alvo) return;

    const aoAlternar = (e: Event) => {
      const evento = e as ToggleEvent;
      const abrindo = evento.newState === "open";
      if (abrindo) {
        const r = alvo.getBoundingClientRect();
        caixa.style.setProperty("--menu-topo", `${Math.round(r.bottom)}px`);
        caixa.style.setProperty("--menu-direita", `${Math.round(window.innerWidth - r.right)}px`);
      }
      setAberto(abrindo);
    };

    // `beforetoggle` e não `toggle`: a posição precisa estar publicada antes da
    // primeira pintura, ou o menu aparece um quadro no canto e salta.
    caixa.addEventListener("beforetoggle", aoAlternar);
    return () => caixa.removeEventListener("beforetoggle", aoAlternar);
  }, []);

  return (
    <>
      <button
        type="button"
        ref={gatilho}
        className={
          emUso
            ? "editor-rico__botao editor-rico__gatilho editor-rico__gatilho--em-uso"
            : "editor-rico__botao editor-rico__gatilho"
        }
        aria-label={rotulo}
        aria-haspopup="true"
        aria-expanded={aberto}
        title={rotulo}
        popoverTarget={id}
        onMouseDown={impedirRoubo}
      >
        <Icone glifo={glifo} papel="acao" />
        <Icone glifo={IconeAbrirMenu} papel="acao" tamanho="p" />
      </button>

      <div id={id} popover="auto" ref={painel} className="editor-rico__menu" aria-label={rotulo}>
        {children}
      </div>
    </>
  );
}

/**
 * Uma seção do menu: a etiqueta visível mais o grupo de alvos.
 *
 * Dentro do menu a etiqueta é **pintada**, e não só acessível. Fora dele, na
 * barra, o nome mora no `aria-label` porque não há espaço para palavra nenhuma;
 * aqui há, e sem ela quatro "A" coloridos e três "A" de tamanhos diferentes
 * ficam um em cima do outro sem dizer qual decisão é qual.
 */
function Secao({
  rotulo,
  segmentado,
  empilhado,
  children,
}: {
  rotulo: string;
  segmentado?: boolean;
  /** Uma opção por linha — o menu da tabela, cujos rótulos são frases. */
  empilhado?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="editor-rico__secao">
      <p className="etiqueta" id={`secao-${rotulo}`}>
        {rotulo}
      </p>
      <div
        className={
          empilhado
            ? "editor-rico__opcoes"
            : segmentado
              ? "editor-rico__grupo editor-rico__grupo--segmentado"
              : "editor-rico__grupo"
        }
        role="group"
        aria-labelledby={`secao-${rotulo}`}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Um grupo da barra: o que é da mesma decisão, junto e visivelmente separado do
 * resto.
 *
 * A separação é **superfície própria**, e não um fio divisor. Os grupos ainda
 * quebram em linha na folha do estreito, e fio em grupo quebrado marca o lugar
 * errado — ele fica pendurado no meio de uma linha, apontando para nada.
 *
 * `segmentado` é para o grupo cujos alvos são degraus de uma mesma escala: eles
 * se encostam, sem vão, e leem como um controle só.
 */
function Grupo({
  rotulo,
  segmentado,
  children,
}: {
  rotulo: string;
  segmentado?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={segmentado ? "editor-rico__grupo editor-rico__grupo--segmentado" : "editor-rico__grupo"}
      role="group"
      aria-label={rotulo}
    >
      {children}
    </div>
  );
}

/**
 * **Todo alvo da barra impede o padrão do `mousedown`.** Sem isto o navegador
 * tira o foco da área de escrita *antes* do comando rodar, e o que se perde no
 * caminho é a seleção — com ela, as marcas armazenadas. Medido no app: cursor
 * num parágrafo vazio, clique em negrito, e o que se digita em seguida vai para
 * o botão, não para a nota.
 *
 * O `focus()` da corrente não salva: ele só devolve cedo — sem tocar na
 * transação — **quando a view ainda tem o foco**. Perdido o foco, o caminho que
 * sobra passa por `setSelection`, e `setSelection` limpa `storedMarks` por
 * definição. Por isso o conserto é não deixar o foco sair, e não recuperá-lo
 * depois.
 *
 * Vale também para o gatilho de menu: o `popovertarget` age no `click`, então
 * impedir o `mousedown` abre o menu do mesmo jeito e deixa a seleção viva para
 * o alvo lá dentro.
 */
const impedirRoubo = (e: React.MouseEvent) => e.preventDefault();

function Botao({
  rotulo,
  atalho,
  glifo,
  ativo,
  desabilitado,
  aoTocar,
}: {
  rotulo: string;
  /** O atalho de teclado, quando há. Entra na dica junto com o nome (Fase 1F). */
  atalho?: string;
  glifo: Parameters<typeof Icone>[0]["glifo"];
  ativo?: boolean;
  /** Desligado quando o comando não se aplica — desfazer sem histórico, por exemplo. */
  desabilitado?: boolean;
  aoTocar: () => void;
}) {
  return (
    <button
      type="button"
      className="editor-rico__botao"
      aria-label={rotulo}
      title={atalho ? `${rotulo} · ${atalho}` : rotulo}
      aria-pressed={ativo ?? false}
      disabled={desabilitado ?? false}
      onMouseDown={impedirRoubo}
      onClick={aoTocar}
    >
      <Icone glifo={glifo} papel="acao" />
    </button>
  );
}

function Amostra({
  classe,
  rotulo,
  ativo,
  aoTocar,
}: {
  classe: string;
  rotulo: string;
  ativo: boolean;
  aoTocar: () => void;
}) {
  return (
    <button
      type="button"
      className={`editor-rico__botao editor-rico__amostra ${classe}`}
      aria-label={rotulo}
      aria-pressed={ativo}
      title={rotulo}
      onMouseDown={impedirRoubo}
      onClick={aoTocar}
    >
      <span aria-hidden="true">A</span>
    </button>
  );
}
